/**
 * Workspace Sidebar Component
 *
 * Main sidebar for the Data Workspace OS experience.
 * Combines folder tree and quick access navigation.
 *
 * Features:
 * - Quick access (All Items, Favorites, Recent)
 * - Hierarchical folder navigation
 * - Trash access
 * - Settings navigation
 */

import { Link, useRouterState } from '@tanstack/react-router';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  SidebarGroup,
  SidebarGroupContent,
} from '@loader/ui';
import { BookOpen, Users, CreditCard, Bell, Settings, FilePlus, MessageCircle, Plug, KeyRound } from 'lucide-react';

import { QuickAccess } from './QuickAccess';
import { FolderTree } from './FolderTree';
import logoDark from '../../../../assets/logo-dark.png';
import logoLight from '../../../../assets/logo-light.png';

import { NavUser } from '@/components/navigation/NavUser';
import { SearchIcon } from '@/components/icons';
import { useTheme } from '@/hooks/useTheme';
import { useWorkspaceMembership, useWorkspace } from '@/hooks';
import { EXTERNAL_URLS } from '@/constants/urls';
import { modKey } from '@/lib/platform';

// ============================================================================
// TYPES
// ============================================================================

interface WorkspaceSidebarProps extends React.ComponentProps<typeof Sidebar> {
  onCreateFolder?: () => void;
  onDeleteFolder?: (id: string) => void;
  onSearch?: () => void;
}

// ============================================================================
// SEARCH BUTTON
// ============================================================================

interface SearchButtonProps {
  onClick?: () => void;
  canUpload?: boolean;
}

function SearchButton({ onClick, canUpload }: SearchButtonProps) {
  return (
    <SidebarGroup className="px-2 py-2">
      <SidebarGroupContent>
        <div className="flex items-center gap-2 group-data-[collapsible=icon]:hidden">
          <button
            onClick={onClick}
            className="border-input bg-background text-muted-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 flex-1 items-center gap-2 rounded-md border px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1"
          >
            <SearchIcon className="size-4 shrink-0 opacity-50" />
            <span className="flex-1 text-left">Search...</span>
            <kbd className="bg-muted text-muted-foreground pointer-events-none ml-auto inline-flex h-5 items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium">
              <span className="text-xs">{modKey}</span>K
            </kbd>
          </button>
          {canUpload && (
            <Link
              to="/versions/new"
              search={{ tableId: undefined }}
              className="border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring flex h-9 w-9 shrink-0 items-center justify-center rounded-md border shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1"
              title="New Version"
            >
              <FilePlus className="size-4 opacity-50" />
            </Link>
          )}
        </div>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

// ============================================================================
// SETTINGS SECTION
// ============================================================================

function SettingsSection() {
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const { isOwner, canEdit } = useWorkspaceMembership();

  const isIntegrationsActive = currentPath === '/inputs';
  const isApiActive = currentPath === '/settings/api';
  const isGeneralActive = currentPath === '/settings/general';
  const isMembersActive = currentPath === '/settings/members';
  const isBillingActive = currentPath === '/settings/billing';
  const isNotificationsActive = currentPath === '/settings/notifications';

  return (
    <>
      {/* Help */}
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                tooltip="Chat support"
                onClick={() => {
                  window.$crisp?.push(['do', 'chat:show']);
                  window.$crisp?.push(['do', 'chat:open']);
                }}
              >
                <MessageCircle className="size-4" />
                <span>Chat support</span>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Documentation">
                <a
                  href={EXTERNAL_URLS.DOCUMENTATION}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <BookOpen className="size-4" />
                  <span>Documentation</span>
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {/* Integrations & API — editors and owners only */}
      {canEdit && (
        <>
          <SidebarSeparator />

          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Integrations" isActive={isIntegrationsActive}>
                    <Link to="/inputs">
                      <Plug className="size-4" />
                      <span>Integrations</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="API" isActive={isApiActive}>
                    <Link to="/settings/api">
                      <KeyRound className="size-4" />
                      <span>API</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </>
      )}

      {/* Owner-only settings */}
      {isOwner && (
        <>
          <SidebarSeparator />

          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="General" isActive={isGeneralActive}>
                    <Link to="/settings/general">
                      <Settings className="size-4" />
                      <span>General</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Members" isActive={isMembersActive}>
                    <Link to="/settings/members">
                      <Users className="size-4" />
                      <span>Members</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Billing" isActive={isBillingActive}>
                    <Link to="/settings/billing">
                      <CreditCard className="size-4" />
                      <span>Billing</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Notifications" isActive={isNotificationsActive}>
                    <Link to="/settings/notifications">
                      <Bell className="size-4" />
                      <span>Notifications</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </>
      )}
    </>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Workspace Sidebar
 *
 * Main navigation sidebar for the Data Workspace OS experience.
 * Replaces the previous AppSidebar with folder-based navigation.
 */
export function WorkspaceSidebar({
  onCreateFolder,
  onDeleteFolder,
  onSearch,
  ...props
}: WorkspaceSidebarProps) {
  const { resolvedTheme } = useTheme();
  const logo = resolvedTheme === 'dark' ? logoDark : logoLight;

  const { canEdit } = useWorkspaceMembership();
  const { workspace } = useWorkspace();

  return (
    <Sidebar collapsible="icon" {...props}>
      {/* Header with branding */}
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/workspace" aria-label="Go to workspace home">
                <img
                  src={logo}
                  alt="Loada.io"
                  className="size-8 transition-all"
                />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">Loada</span>
                  <span className="text-muted-foreground truncate text-xs">{workspace?.name ?? 'Workspace'}</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* Main content */}
      <SidebarContent className="flex flex-col">
        {/* Search button + New Version */}
        <SearchButton onClick={onSearch} canUpload={canEdit} />

        {/* Smart folders: Favorites, Recent, Trash */}
        <QuickAccess />

        <SidebarSeparator className="group-data-[collapsible=icon]:hidden [@media(max-height:700px)]:hidden" />

        {/* Folder tree - expands to fill space, scrolls if needed */}
        {/* Hidden when collapsed (not usable in icon mode) or when viewport is short */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden group-data-[collapsible=icon]:hidden [@media(max-height:700px)]:hidden">
          <FolderTree
            canEdit={canEdit}
            onCreateFolder={onCreateFolder}
            onDeleteFolder={onDeleteFolder}
          />
        </div>

        {/* Settings section - fixed at bottom */}
        <div className="mt-auto shrink-0">
          <SidebarSeparator className="group-data-[collapsible=icon]:hidden" />
          <SettingsSection />
        </div>
      </SidebarContent>

      {/* Footer with user menu */}
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>

      {/* Rail for collapsed state */}
      <SidebarRail />
    </Sidebar>
  );
}
